import React from 'react';
import {
  backgroundColorOptions,
  textColorOptions,
  accentColorOptions
} from './theme/colors';

const generateStars = (count = 5, accent) => {
  const total = Math.max(0, Math.min(count, 5));
  return Array.from({ length: total }).map((_, index) => (
    <svg key={index} className={`w-4 h-4 ${accent.textClass || 'text-amber-400'}`} fill="currentColor" viewBox="0 0 20 20" style={accent.style}>
      <path d="M9.049 2.927a1 1 0 011.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.587 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.922-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.196-1.538-1.118l1.07-3.292a1 1 0 00-.363-1.118L2.98 8.72c-.784-.57-.382-1.81.587-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
    </svg>
  ));
};

const TestimonialComponent = ({
  quote = 'Puck lets our product team move at the speed of thought. We can design, ship and iterate on marketing pages without waiting on engineering.',
  author = 'Sofia Alvarez',
  role = 'Head of Product',
  company = 'Velocity Inc.',
  avatar = 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=facearea&w=200&q=80',
  layout = 'centered',
  backgroundColor = 'white',
  accentColor = 'blue',
  textColor = 'slate-900',
  showAvatar = true,
  showRating = false,
  rating = 5,
  quoteSize = 'xl'
}) => {
  // Removed color resolution - now using Tailwind dark mode classes
  const accent = { textClass: 'text-amber-400' }; // Keep accent for stars

  const quoteSizes = {
    lg: 'text-2xl md:text-3xl',
    xl: 'text-3xl md:text-4xl',
    '2xl': 'text-4xl md:text-5xl'
  };

  const isCentered = layout === 'centered';

  return (
    <section className="w-full bg-white dark:bg-slate-900">
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <div className={`flex flex-col gap-8 ${isCentered ? 'items-center text-center' : 'items-start text-left'}`}>
          {showRating && (
            <div className={`flex ${isCentered ? 'justify-center' : ''} gap-1`}>{generateStars(rating, accent)}</div>
          )}

          <blockquote className={`font-serif leading-tight ${quoteSizes[quoteSize] || quoteSizes.xl} text-slate-900 dark:text-slate-100`}>
            “{quote}”
          </blockquote>

          <div className={`flex ${isCentered ? 'justify-center' : ''} items-center gap-4`}>
            {showAvatar && avatar && (
              <img
                src={avatar}
                alt={author}
                className="w-14 h-14 rounded-full object-cover border border-white/60 dark:border-slate-700 shadow-xs"
              />
            )}
            <div className="text-left">
              <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {author}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-300">
                {[role, company].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Testimonial = {
  fields: {
    quote: {
      type: 'textarea',
      label: 'Zitat'
    },
    quoteSize: {
      type: 'select',
      label: 'Zitatgröße',
      options: [
        { label: 'Groß', value: 'lg' },
        { label: 'Sehr groß', value: 'xl' },
        { label: 'Riesig', value: '2xl' }
      ]
    },
    author: {
      type: 'text',
      label: 'Name'
    },
    role: {
      type: 'text',
      label: 'Rolle'
    },
    company: {
      type: 'text',
      label: 'Unternehmen'
    },
    avatar: {
      type: 'text',
      label: 'Avatar URL'
    },
    layout: {
      type: 'select',
      label: 'Layout',
      options: [
        { label: 'Zentriert', value: 'centered' },
        { label: 'Linksbündig', value: 'left' }
      ]
    },
    backgroundColor: {
      type: 'select',
      label: 'Hintergrundfarbe',
      options: backgroundColorOptions()
    },
    accentColor: {
      type: 'select',
      label: 'Akzentfarbe',
      options: accentColorOptions()
    },
    textColor: {
      type: 'select',
      label: 'Textfarbe',
      options: textColorOptions()
    },
    showAvatar: {
      type: 'radio',
      label: 'Avatar anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    showRating: {
      type: 'radio',
      label: 'Sternebewertung anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    rating: {
      type: 'number',
      label: 'Bewertung (0-5)',
      min: 0,
      max: 5,
      step: 1,
      condition: (values) => values.showRating === true
    }
  },
  defaultProps: {
    quote: 'Puck lets our product team move at the speed of thought. We can design, ship and iterate on marketing pages without waiting on engineering.',
    quoteSize: 'xl',
    author: 'Sofia Alvarez',
    role: 'Head of Product',
    company: 'Velocity Inc.',
    avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=facearea&w=200&q=80',
    layout: 'centered',
    backgroundColor: 'white',
    accentColor: 'motion-yellow',
    textColor: 'slate-900',
    showAvatar: true,
    showRating: false,
    rating: 5
  },
  render: TestimonialComponent
};
